/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from 'react';
import UseAxios from '../../../Api/http/http';
import { Typography, IconButton, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PropTypes from 'prop-types';
import SearchDropdown from '../../../Components/Dropdown/dropdown';
import { EDIT_TARGET, SET_TARGET } from '../../../Constants/strings';
import './quarterTarget.scss';
import { certificationList } from '../../../Api/request/certification';
import { getClassNames } from '../SetTarget/setTargetUtils';

// The QuarterTarget component for managing quarterly targets
const QuarterTarget = ({
  quarter,
  targets,
  onTargetChange,
  onDeleteTarget,
  setIsDirty,
  setIsErrorMsg,
}) => {
  // State hooks for managing component state
  const [newCertification, setNewCertification] = useState('');
  const [newTarget, setNewTarget] = useState('');
  const [newCertValue, setNewCertValue] = useState('');
  const [newTargetValue, setNewTargetValue] = useState('');
  const [certifications, setCertifications] = useState([]);
  const [filteredCertifications, setFilteredCertifications] = useState([]);
  const [targetError, setTargetError] = useState('');
  const [editTargetError, setEditTargetError] = useState('');
  const [certificationError, setCertificationError] = useState('');
  const [isRow, setIsRow] = useState(false);

  useEffect(() => {
    UseAxios(certificationList())
      .then((response) => {
        const result = response.data.certificationDetails
          .filter((certificate) => certificate.isActive)
          .map((certificate) => ({
            id: certificate.certificationId,
            name: certificate.certificationName,
          }));
        setCertifications(result);
        filterCertifications(result, targets);
      })
      .catch((error) => {
        console.error('certification error', error);
      });
  }, []);

  useEffect(() => {
    filterCertifications(certifications, targets);
  }, [targets]);
  const filterCertifications = (certificationsList, targetsList) => {
    const selectedCertifications = targetsList.map(
      (target) => target.certification.name,
    );
    const filtered = certificationsList.filter(
      (cert) => !selectedCertifications.includes(cert.name),
    );
    setFilteredCertifications(filtered);
  };

  useEffect(() => {
    const hasNonEmptyValue = (errorObj) => {
      for (const key in errorObj) {
        if (errorObj[key] === '') {
          return false;
        }
      }
      return true;
    };
    if (
      (targetError != '' && hasNonEmptyValue(targetError))
      || (editTargetError != '' && hasNonEmptyValue(editTargetError))
      || (certificationError != '' && hasNonEmptyValue(certificationError))
    ) {
      setIsErrorMsg(true);
    } else {
      setIsErrorMsg(false);
    }
  }, [targetError, editTargetError, certificationError]);

  // Function to handle input change for newTarget
  const handleTargetInputChange = (event) => {
    const value = event.target.value;
    setNewTargetValue(value);
    validateTargetName(value);
    targets.push({
      certification: '',
      target: Number(value) ? Number(value) : '',
      isUpdated: true,
      isInput: true,
    });
    onTargetChange(targets);
    setIsRow(true);
    setNewTarget('');
  };

  const handleCertChange = (certificationValue) => {
    setNewCertValue(certificationValue);
    setCertificationError('');
    const updatedTargets = targets.map((targetItem, item) =>
      item === targets.length - 1
        ? { ...targetItem, certification: certificationValue }
        : targetItem,
    );
    onTargetChange(updatedTargets);
    setEditTargetError('');
    setNewCertification('');
  };

  const handleTargetsChange = (targetValue) => {
    setNewTargetValue(targetValue);
    if (!targetValue.match(/^\d*$/)) {
      setTargetError((prevErrors) => ({
        ...prevErrors,
        [targets.length - 1]: SET_TARGET.ENTER_VALID_NUMBER,
      }));
      return;
    }
    const targetInt = targetValue === '' ? 0 : parseInt(targetValue, 10);
    const updatedTargets = targets.map((targetItem, item) =>
      item === targets.length - 1
        ? { ...targetItem, target: targetInt, isUpdated: true }
        : targetItem,
    );
    onTargetChange(updatedTargets);
    setNewTarget('');
    setTargetError((prevErrors) => ({
      ...prevErrors,
      [targets.length - 1]: '',
    }));
  };

  useEffect(() => {}, [targetError, editTargetError]);
  // Validation for target name
  const validateTargetName = (value) => {
    if (value.match(/^\d*$/)) {
      setNewTarget(value);
      setTargetError('');
    } else {
      setTargetError((prevErrors) => ({
        ...prevErrors,
        [targets.length - 1]: SET_TARGET.ENTER_VALID_NUMBER,
      }));
    }
  };
  // Function to handle input change for newCertification
  const handleCertificationInputChange = (event) => {
    const value = event.target.value;
    setNewCertValue(value);
    setCertificationError('');
    targets.push({
      certification: value,
      target: 0,
      isUpdated: true,
      isInput: true,
    });
    onTargetChange(targets);
    setIsRow(true);
    setNewCertification('');
    setNewTarget('');
    setCertificationError('');
    setTargetError('');
  };

  // Function to handle adding a new target
  const handleAddTarget = () => {
    if (!newCertValue) {
      setCertificationError((prevErrors) => ({
        ...prevErrors,
        [targets.length - 1]: `${SET_TARGET.REQUIRED_FILED}`,
      }));
      return;
    }
    setCertificationError('');
    const targetInt = newTargetValue === '' ? 0 : parseInt(newTargetValue, 10);
    if (newCertValue && !isNaN(targetInt)) {
      const updatedTargets = targets.map((targetItem, item) =>
        item === targets.length - 1
          ? { ...targetItem, isInput: false }
          : targetItem,
      );
      onTargetChange(updatedTargets);
      setIsRow(false);
      setNewTargetValue('');
      setTargetError('');
      setEditTargetError('');
    }
  };

  // Function to handle removing a target
  const handleRemoveTarget = (targetIndex, targetId) => {
    onDeleteTarget(targetId);
    onTargetChange(
      targets.filter((targetItem, index) => index !== targetIndex),
    );
    setIsDirty(true);
  };

  // Function to update a target's certification
  const handleCertificationChange = (index, certificationValue) => {
    const updatedTargets = targets.map((targetItem, item) =>
      item === index
        ? { ...targetItem, certification: certificationValue, isUpdated: true }
        : targetItem,
    );
    onTargetChange(updatedTargets);
    setNewCertification('');
  };

  // Function to update a target's target value
  const handleTargetValueChange = (index, targetValue) => {
    if (!targetValue.match(/^\d*$/)) {
      setEditTargetError((prevErrors) => ({
        ...prevErrors,
        [index]: SET_TARGET.ENTER_VALID_NUMBER,
      }));
      return;
    }
    const targetInt = targetValue === '' ? 0 : parseInt(targetValue, 10);
    const updatedTargets = targets.map((targetItem, item) =>
      item === index
        ? { ...targetItem, target: targetInt, isUpdated: true }
        : targetItem,
    );
    onTargetChange(updatedTargets);
    setEditTargetError((prevErrors) => ({
      ...prevErrors,
      [index]: '',
    }));
  };

  // Function to calculate the total of all targets for the quarter
  const calculateQuarterTarget = () => {
    return targets.reduce((sum, targetItem) => sum + targetItem.target, 0);
  };

  // Render the component UI
  return (
    <div className="quarter-targets">
      <Typography className="quarter-title-text" variant="subtitle2">
        {EDIT_TARGET.TARGET_QUARTER} {quarter} ({calculateQuarterTarget()})
      </Typography>
      {targets.some((targetItem) => targetItem.isInput) ? (
        <div className="quarter-set-target" key={`${targets.length - 1}-${1}`}>
          <div className="dropdown-container">
            <SearchDropdown
              options={filteredCertifications}
              placeholder={EDIT_TARGET.SELECT_CERTIFICATION}
              value={targets[targets.length - 1].certification}
              onChange={(event) => {
                handleCertChange(event.target.value);
                setCertificationError('');
              }}
              dropdownCss="target-dropdown-css"
              clearIcon={true}
            />
            {certificationError && (
              <div className="target-error">
                <small>{certificationError[targets.length - 1]}</small>
              </div>
            )}
          </div>
          <div>
            <div className="target-input">
              <TextField
                placeholder={SET_TARGET.SET_TARGET}
                value={targets[targets.length - 1].target}
                onChange={(event) => handleTargetsChange(event.target.value)}
                size="small"
              />
              <IconButton color="primary" onClick={handleAddTarget}>
                <AddIcon />
              </IconButton>
            </div>
            {targetError[targets.length - 1] && (
              <div className="target-error">
                <small className="target-error-msg">
                  {targetError[targets.length - 1]}
                </small>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="quarter-set-target">
          <div className="dropdown-container">
            <SearchDropdown
              options={filteredCertifications}
              placeholder={EDIT_TARGET.SELECT_CERTIFICATION}
              value={newCertification}
              onChange={handleCertificationInputChange}
              dropdownCss="target-dropdown-css"
              clearIcon={true}
            />
            {certificationError && (
              <div className="target-error">
                <small>{certificationError[targets.length - 1]}</small>
              </div>
            )}
          </div>
          <div>
            <div className="target-input">
              <TextField
                placeholder={SET_TARGET.SET_TARGET}
                value={newTarget}
                onChange={handleTargetInputChange}
                size="small"
              />
              <IconButton color="primary" onClick={handleAddTarget}>
                <AddIcon />
              </IconButton>
            </div>
            {targetError && (
              <div className="target-error">
                <small className="target-error-msg">
                  {targetError[targets.length - 1]}
                </small>
              </div>
            )}
          </div>
        </div>
      )}
      {targets
        .slice()
        .reverse()
        .filter((targetItem) => !targetItem.isInput)
        .map(
          (
            targetItem,
            index, // Map through targets to display them
          ) => (
            <div className="edit-target-row" key={`${quarter}-${index}`}>
              <div
                className={`dropdown-container ${getClassNames(
                  targetItem.targetDetails,
                )}`}
              >
                <SearchDropdown
                  options={filteredCertifications}
                  value={targetItem.certification}
                  onChange={(event) => {
                    handleCertificationChange(
                      isRow
                        ? targets.length - 1 - (index + 1)
                        : targets.length - 1 - index,
                      event.target.value,
                    );
                    setCertificationError('');
                  }}
                  dropdownCss="target-dropdown-css"
                />
              </div>
              <div>
                <div
                  className={`target-input ${getClassNames(
                    targetItem.targetDetails,
                  )}`}
                >
                  <TextField
                    value={targetItem.target}
                    onChange={(event) =>
                      handleTargetValueChange(
                        isRow
                          ? targets.length - 1 - (index + 1)
                          : targets.length - 1 - index,
                        event.target.value,
                      )
                    }
                    size="small"
                  />
                  <IconButton
                    color="primary"
                    onClick={() =>
                      handleRemoveTarget(
                        isRow
                          ? targets.length - 1 - (index + 1)
                          : targets.length - 1 - index,
                        targetItem.targetId,
                      )
                    }
                  >
                    <RemoveIcon />
                  </IconButton>
                </div>
                {editTargetError[
                  isRow
                    ? targets.length - 1 - (index + 1)
                    : targets.length - 1 - index
                ] && (
                  <div className="target-error">
                    <small className="target-error-msg">
                      {
                        editTargetError[
                          isRow
                            ? targets.length - 1 - (index + 1)
                            : targets.length - 1 - index
                        ]
                      }
                    </small>
                  </div>
                )}
              </div>
            </div>
          ),
        )}
    </div>
  );
};

// Prop types for component validation
QuarterTarget.propTypes = {
  quarter: PropTypes.number.isRequired,
  targets: PropTypes.array.isRequired,
  onTargetChange: PropTypes.func.isRequired,
  onDeleteTarget: PropTypes.func.isRequired,
  setIsDirty: PropTypes.func,
  setIsErrorMsg: PropTypes.func,
};

export default QuarterTarget;
